/**
 * PDF Rendering Utility
 *
 * Uses PDF.js in the browser only. PDF.js is loaded from CDN so the npm
 * `pdfjs-dist` package (and its optional native `canvas` dependency) is not
 * required at install time — avoiding node-gyp failures on Vercel.
 */

const PDFJS_VERSION = '3.11.174';
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;

let pdfjsLoadPromise = null;

/**
 * @returns {Promise<object>}
 */
async function getPdfJsLib() {
  if (typeof window !== 'undefined' && window.pdfjsLib) {
    return window.pdfjsLib;
  }
  if (pdfjsLoadPromise) {
    return pdfjsLoadPromise;
  }
  pdfjsLoadPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('PDF.js can only be loaded in a browser environment'));
      return;
    }
    const existing = document.querySelector(`script[data-pdfjs-loader="${PDFJS_VERSION}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.pdfjsLib));
      existing.addEventListener('error', () => reject(new Error('Failed to load PDF.js')));
      return;
    }
    const script = document.createElement('script');
    script.src = `${PDFJS_BASE}/pdf.min.js`;
    script.async = true;
    script.dataset.pdfjsLoader = PDFJS_VERSION;
    script.onload = () => {
      const lib = window.pdfjsLib;
      if (!lib) {
        reject(new Error('pdfjsLib global missing after script load'));
        return;
      }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
      resolve(lib);
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js script'));
    document.head.appendChild(script);
  });
  return pdfjsLoadPromise;
}

/**
 * Renders a specific page of a PDF to a canvas/image URL.
 *
 * @param {File|string} fileOrUrl - PDF File object or URL
 * @param {number} pageNumber - Page number to render (1-indexed)
 * @param {number} scale - Render scale (2 = good quality for cropping)
 * @returns {Promise<Object>} Object with canvas, imageUrl, page info
 */
export async function renderPdfPageToCanvas(fileOrUrl, pageNumber = 1, scale = 2) {
  try {
    const pdfjsLib = await getPdfJsLib();

    let pdfSource;
    if (fileOrUrl instanceof File) {
      const arrayBuffer = await fileOrUrl.arrayBuffer();
      pdfSource = { data: arrayBuffer };
    } else if (typeof fileOrUrl === 'string') {
      pdfSource = fileOrUrl;
    } else {
      throw new Error('Invalid PDF source');
    }

    const loadingTask = pdfjsLib.getDocument(pdfSource);
    const pdf = await loadingTask.promise;

    const totalPages = pdf.numPages;
    const validPageNumber = Math.min(Math.max(1, pageNumber), totalPages);

    const page = await pdf.getPage(validPageNumber);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = {
      canvasContext: ctx,
      viewport,
    };

    await page.render(renderContext).promise;

    const imageUrl = canvas.toDataURL('image/jpeg', 0.95);

    return {
      canvas,
      imageUrl,
      pageNumber: validPageNumber,
      totalPages,
      renderedWidth: canvas.width,
      renderedHeight: canvas.height,
      scale,
    };
  } catch (error) {
    console.error('PDF rendering error:', error);
    throw new Error(`Failed to render PDF page: ${error.message}`);
  }
}

/**
 * Gets the total number of pages in a PDF.
 *
 * @param {File|string} fileOrUrl - PDF File object or URL
 * @returns {Promise<number>} Total page count
 */
export async function getPdfPageCount(fileOrUrl) {
  try {
    const pdfjsLib = await getPdfJsLib();

    let pdfSource;
    if (fileOrUrl instanceof File) {
      const arrayBuffer = await fileOrUrl.arrayBuffer();
      pdfSource = { data: arrayBuffer };
    } else {
      pdfSource = fileOrUrl;
    }

    const loadingTask = pdfjsLib.getDocument(pdfSource);
    const pdf = await loadingTask.promise;

    return pdf.numPages;
  } catch (error) {
    console.error('PDF page count error:', error);
    return 1;
  }
}

/**
 * Creates a preview image from the first page of a PDF.
 *
 * @param {File} file - PDF File object
 * @param {number} scale - Render scale
 * @returns {Promise<string>} Image data URL
 */
export async function createPdfPreview(file, scale = 1.5) {
  const result = await renderPdfPageToCanvas(file, 1, scale);
  return result.imageUrl;
}

export default {
  renderPdfPageToCanvas,
  getPdfPageCount,
  createPdfPreview,
};
